/**
 * RetailKey (POS) module — core (TASK-66, WAVE 1) — shared types.
 *
 * Grounded in docs/retailkey-pos/02-backend-design.md §2 (data model) and §3-4
 * (sell transaction + endpoint catalogue). Covers Products, lookups
 * (Departments/Categories/Brands/Tags), Stock adjustments, POS settings, and the
 * sell terminal (PosSale + PosSaleItem) with dual settlement.
 *
 * Money convention: stored as Postgres DECIMAL(12,2). Monetary fields are exposed
 * here as strings (the on-the-wire shape — read with parseFloat, write .toFixed(2)).
 * Ids and timestamps are strings (UUID / ISO). Counts/quantities are numbers.
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Settlement path for a POS sale (pos_sale_settlement_enum). */
export enum PosSettlementType {
  CHARGE_TO_ROOM = 'charge_to_room',
  CASH = 'cash',
  CARD = 'card',
  /** WAVE 2 (F3) — charge the sale to a RetailKey house account. */
  HOUSE_ACCOUNT = 'house_account',
}

/** Lifecycle status of a POS sale (pos_sale_status_enum). Only `completed` is
 *  written this wave; `voided` is reserved (no void endpoint in wave 1). */
export enum PosSaleStatus {
  COMPLETED = 'completed',
  VOIDED = 'voided',
}

/** The four lightweight lookup kinds backing products. */
export enum RetailLookupType {
  DEPARTMENT = 'department',
  CATEGORY = 'category',
  BRAND = 'brand',
  TAG = 'tag',
}

/** Reason recorded on a stock_adjustments ledger row. `sale` is written by the
 *  sell transaction (negative delta per sold line); the rest are manual. */
export enum StockAdjustmentReason {
  RECEIVED = 'received',
  DAMAGED = 'damaged',
  CORRECTION = 'correction',
  SHRINKAGE = 'shrinkage',
  SALE = 'sale',
  OTHER = 'other',
}

// ---------------------------------------------------------------------------
// Catalog — products + lookups
// ---------------------------------------------------------------------------

export interface RetailProduct {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  sku: string;
  /** Optional 12-13 digit barcode; validated if supplied, never auto-generated. */
  upc?: string | null;
  /** DECIMAL(12,2) as string. */
  price: string;
  departmentId: string;
  categoryId?: string | null;
  brandId?: string | null;
  /** Array of retail_tags ids (no junction table this wave). */
  tags: string[];
  /** Drives the single aggregated tax line on a sale. */
  taxable: boolean;
  isActive: boolean;
  /** On-hand inventory count (the atomic-decrement target). */
  stockOnHand: number;
  /** WAVE 2 (F7) — menu section for terminal grid grouping (null = ungrouped). */
  menuSectionId?: string | null;
  /** WAVE 2 (F7) — allergen lookup ids attached to this product. */
  allergens?: string[];
  /** WAVE 2 (F7) — per-product nutrition detail (null when none set). */
  nutrients?: ProductNutrients | null;
  createdAt: string;
  updatedAt: string;
}

/** One row from any of the four lookup tables (department/category/brand/tag). */
export interface RetailLookup {
  id: string;
  type: RetailLookupType;
  tenantId: string;
  propertyId: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Stock
// ---------------------------------------------------------------------------

/** Append-only audit ledger row explaining an on-hand change. */
export interface StockAdjustment {
  id: string;
  tenantId: string;
  propertyId: string;
  productId: string;
  /** Signed: +received, -removed. Never zero. */
  delta: number;
  reason: StockAdjustmentReason;
  note?: string | null;
  /** On-hand after applying this adjustment (snapshot). */
  balanceAfter: number;
  /** Actor user id. */
  createdBy?: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// POS settings
// ---------------------------------------------------------------------------

/** Per-property POS configuration. Tax is a flat rate applied to the taxable
 *  subtotal (the "simple" tax line the scope calls for). */
export interface RetailPosSettings {
  propertyId: string;
  /** Flat tax rate as a percent, DECIMAL stored as string (e.g. "20.00"). */
  taxRatePercent: string;
  /** Optional receipt header/footer text. */
  receiptHeader?: string | null;
  receiptFooter?: string | null;
  currency?: string | null;

  // --- WAVE 2 (F5) App-Config: POS tab ---
  /** Terminal product layout: 'grid' | 'list'. */
  defaultTerminalView?: 'grid' | 'list' | null;
  /** Age gate for restricted products (years); null = no gate. */
  minAge?: number | null;
  /** Bottle deposit applied per applicable item, DECIMAL(12,2) as string. */
  bottleDeposit?: string | null;
  /** Whether an external payment gateway is enabled for card tenders. */
  paymentGatewayEnabled?: boolean | null;

  // --- WAVE 2 (F5) App-Config: Order tab ---
  orderNumberPrefix?: string | null;
  autoPrintReceipt?: boolean | null;
  requireCustomerName?: boolean | null;
  allowDiscounts?: boolean | null;
  /** Max discount percent (0-100), DECIMAL(5,2) as string. */
  maxDiscountPercent?: string | null;

  // --- WAVE 2 (F5) App-Config: Night Audit tab ---
  autoRunNightAudit?: boolean | null;
  /** Time-of-day "HH:mm[:ss]" for the auto-run; null = manual only. */
  autoRunTime?: string | null;
  warnOnOpenShifts?: boolean | null;
  /** Validated email recipients for the night-audit report. */
  auditRecipients?: string[] | null;

  // --- WAVE 2 (F5) App-Config: Shift tab ---
  /** Alert threshold for a cash drop, DECIMAL(12,2) as string; null = no alert. */
  cashDropAlertThreshold?: string | null;
  blindDrop?: boolean | null;
  requireShiftOnSale?: boolean | null;
}

// ---------------------------------------------------------------------------
// Sales ledger
// ---------------------------------------------------------------------------

/** A line on a sale — a snapshot, immune to later product edits/deletes. */
export interface PosSaleItem {
  id: string;
  saleId: string;
  /** Nullable: the snapshot survives if the product is later deleted. */
  productId?: string | null;
  name: string;
  sku: string;
  /** DECIMAL(12,2) as string — price at sale time. */
  unitPrice: string;
  qty: number;
  /** DECIMAL(12,2) as string — unitPrice * qty. */
  lineTotal: string;
  taxable: boolean;
}

export interface PosSale {
  id: string;
  tenantId: string;
  propertyId: string;
  /** Human receipt number, unique per property. */
  saleNumber: string;
  settlementType: PosSettlementType;
  /** DECIMAL(12,2) as string. */
  subtotal: string;
  taxTotal: string;
  total: string;
  currency: string;
  status: PosSaleStatus;
  /** charge_to_room only. */
  bookingId?: string | null;
  /** The posted folio line id (charge_to_room). */
  folioItemId?: string | null;
  /** The RetailKey Charge Type used (charge_to_room). */
  chargeTypeId?: string | null;
  /** cash/card only — amount tendered, DECIMAL(12,2) as string. */
  tenderAmount?: string | null;
  /** cash/card only — change due, DECIMAL(12,2) as string. */
  changeDue?: string | null;
  createdBy?: string | null;
  createdAt: string;
  /** Populated on detail/receipt reads. */
  items?: PosSaleItem[];
}

// ---------------------------------------------------------------------------
// Request / response helpers (endpoint catalogue §4)
// ---------------------------------------------------------------------------

/** Cart line on a confirm-sale request. Server re-reads price under lock. */
export interface ConfirmSaleItem {
  productId: string;
  qty: number;
}

/** POST /v1/retail/pos/sales — client never sends prices/totals (server computes). */
export interface ConfirmSaleRequest {
  propertyId: string;
  items: ConfirmSaleItem[];
  settlement: {
    type: PosSettlementType;
    /** charge_to_room only — the in-house booking to post to. */
    bookingId?: string;
    /** house_account only — the RetailKey house account to charge (F3). */
    accountId?: string;
    /** cash/card only — amount tendered. */
    tenderAmount?: number;
    /** cash/card only — the tender method. */
    method?: 'cash' | 'card';
  };
}

/** A receipt line for display (GET receipt / confirm response). */
export interface SaleReceiptItem {
  name: string;
  sku: string;
  qty: number;
  unitPrice: string;
  lineTotal: string;
}

/** Settlement block on a receipt — shape varies by settlement type. */
export interface SaleReceiptSettlement {
  type: PosSettlementType;
  /** charge_to_room. */
  bookingId?: string | null;
  guestName?: string | null;
  room?: string | null;
  folioItemId?: string | null;
  /** cash/card. */
  method?: 'cash' | 'card' | null;
  tenderAmount?: string | null;
  changeDue?: string | null;
  /** house_account (F3) — the RetailKey account charged + post-charge balance. */
  accountId?: string | null;
  houseAccountName?: string | null;
  newBalance?: string | null;
}

/** GET /v1/retail/pos/sales/:id and the confirm response. */
export interface SaleReceipt {
  id: string;
  saleNumber: string;
  createdAt: string;
  operator?: string | null;
  propertyId: string;
  currency: string;
  items: SaleReceiptItem[];
  subtotal: string;
  taxTotal: string;
  total: string;
  settlement: SaleReceiptSettlement;
}

// ===========================================================================
// WAVE 2 — RetailKey (TASK-66) shared types
// ===========================================================================

// ---------------------------------------------------------------------------
// Enums (WAVE 2)
// ---------------------------------------------------------------------------

/** Direction of a retail_house_account_items ledger row (retail_ha_direction_enum).
 *  charge → +amount, settle → -amount. Distinct from the hotel-house-account
 *  `HouseAccountItemDirection` (frontdesk-ops) which uses charge/credit. */
export enum RetailHouseAccountItemDirection {
  CHARGE = 'charge',
  SETTLE = 'settle',
}

/** Report-builder dimension keys (server-side whitelist). The client supplies
 *  only these enum keys; each maps to a fixed server SQL fragment. */
export enum ReportDimension {
  CATEGORY = 'category',
  PRODUCT = 'product',
  SKU = 'sku',
  DAY = 'day',
  SETTLEMENT_TYPE = 'settlement_type',
  CASHIER = 'cashier',
}

/** Report-builder measure keys (server-side whitelist). */
export enum ReportMeasure {
  QTY = 'qty',
  GROSS = 'gross',
  TAX = 'tax',
  NET = 'net',
  DISCOUNT = 'discount',
}

/** Activity-log event kinds — a derived UNION feed (no dedicated table). */
export enum RetailActivityType {
  SALE = 'sale',
  PAID_OUT = 'paid_out',
  STOCK_RECEIPT = 'stock_receipt',
  CONFIG_CHANGE = 'config_change',
}

// ---------------------------------------------------------------------------
// Paid outs (F1)
// ---------------------------------------------------------------------------

/** An immutable till-cash disbursement (pos_paid_outs). Append-only. */
export interface PosPaidOut {
  id: string;
  tenantId: string;
  propertyId: string;
  /** DECIMAL(12,2) as string — positive disbursement. */
  amount: string;
  reason: string;
  recipient: string;
  /** DATE (client-supplied, defaults today). */
  entryDate: string;
  note?: string | null;
  createdBy?: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// RetailKey house accounts (F2)
// ---------------------------------------------------------------------------

/** A POS-scoped, property-scoped charge account (retail_house_accounts). */
export interface RetailHouseAccount {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  /** Short ref, unique per property. */
  code: string;
  description?: string | null;
  /** DECIMAL(12,2) as string — running balance, never negative. */
  balance: string;
  isActive: boolean;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** An append-only ledger line on a retail house account (retail_house_account_items). */
export interface RetailHouseAccountItem {
  id: string;
  tenantId: string;
  accountId: string;
  direction: RetailHouseAccountItemDirection;
  /** DECIMAL(12,2) as string. */
  amount: string;
  /** Set when a charge is a POS sale (F3). */
  saleId?: string | null;
  note?: string | null;
  /** DATE. */
  entryDate: string;
  /** DECIMAL(12,2) as string — balance snapshot post-apply. */
  balanceAfter: string;
  postedBy?: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Vendors (F4)
// ---------------------------------------------------------------------------

/** A supplier directory entry (vendors). */
export interface Vendor {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  /** e.g. "Net 30". */
  terms?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Product attributes (F7)
// ---------------------------------------------------------------------------

/** An allergen lookup row (retail_allergens). */
export interface RetailAllergen {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A single extra nutrient label/value pair. */
export interface ProductNutrientExtra {
  label: string;
  value: string;
}

/** Per-product nutrition detail (product_nutrients) — one row per product. */
export interface ProductNutrients {
  id: string;
  tenantId: string;
  productId: string;
  calories?: number | null;
  servingSize?: string | null;
  /** Validated key/value list. */
  extra: ProductNutrientExtra[];
}

/** A menu section lookup row (retail_menu_sections) — grid grouping/ordering. */
export interface RetailMenuSection {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  /** Grid ordering. */
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Reports & logs (F6)
// ---------------------------------------------------------------------------

/** A saved report-builder definition (saved_report_definitions). */
export interface SavedReportDefinition {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  /** The validated builder config (re-validated on every load/re-run). */
  config: object;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A whitelisted-key/value filter on a report run. */
export interface ReportFilter {
  key: string;
  value: string;
}

/** POST /v1/retail/reports/run — enum keys only (server maps to fixed SQL). */
export interface ReportRunRequest {
  dimensions: ReportDimension[];
  measures: ReportMeasure[];
  /** Inclusive date range bounds (ISO date). */
  dateFrom: string;
  dateTo: string;
  filters?: ReportFilter[];
}

/** The aggregated result of a report run. Money measures as strings, qty as number. */
export interface ReportRunResponse {
  rows: Array<Record<string, string | number>>;
  totals: Record<string, string | number>;
}
